<template>
  <div class="spots-selector" @paste="onPaste">
    <el-select
      v-model="value"
      :class="{ desktop: isDesktop }"
      multiple
      filterable
      :disabled="disabled"
      remote
      reserve-keyword
      placeholder="Enter 2 or more characters"
      no-data-text="Could not find and spots matching your query"
      no-match-text="Could not find any spots for your query"
      loading-text="Loading..."
      :remote-method="lookup"
      :loading="loading"
      @update:modelValue="update"
    >
      <el-option v-if="options.length > 0" key="all" label="All" :value="-1" />
      <el-option
        v-for="item in options"
        :key="item.id"
        :label="item.text"
        :value="item.id"
      />
    </el-select>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  reactive,
  onMounted,
  watchEffect,
  watch,
  PropType
} from 'vue'
import { ISpotsPayload, ISpot } from '@/apps/traforama/interfaces/spots'
import { useSpotsStore } from '@/apps/traforama/store/spots'
import useResize from '@/hooks/useResize'
import { ElMessage } from 'element-plus'

const ID_BATCH_SIZE = 200
const SKIPPED_SHOWN_LIMIT = 20

const parseIdsList = (text: string): Array<number> | null => {
  const tokens = text.split(/[\s,;]+/).filter(Boolean)
  if (tokens.length < 2 || tokens.some((token) => !/^\d+$/.test(token))) {
    return null
  }
  return [...new Set(tokens.map(Number))]
}

interface Option {
  id: number
  text: string
}

export default defineComponent({
  name: 'SpotsSelector',
  props: {
    modelValue: {
      type: Array,
      required: true
    },
    stream: {
      type: Number,
      default: 0
    },
    typeId: {
      type: [Number, Array] as PropType<number | Array<number>>,
      default: 0
    },
    disabled: {
      type: Boolean,
      default: false
    },
    ignoreStreamFilter: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const value = ref(props.modelValue)
    const spotsStore = useSpotsStore()
    const loading = ref(false)
    const options = reactive<Array<Option>>([])
    const currentQuery = ref('')

    const loadInitial = async (leftList: boolean) => {
      if (props.modelValue.length < 1) {
        return
      }
      const payload = {
        filter: {
          id: props.modelValue as Array<number>
        },
        limit: props.modelValue.length,
        start: 0
      } as ISpotsPayload
      const response = await spotsStore.SearchItems(payload)
      assignOptions(response, leftList)
    }

    watch(
      (_: any) => props.modelValue,
      (newValue: any, oldVal: any) => {
        value.value = newValue
        if (props.modelValue.length < 1) {
          assignOptions([], false)
        } else {
          loadInitial(false)
        }
      }
    )

    const assignOptions = (items: Array<ISpot>, leftList: boolean) => {
      const newOptions = items.map((item: ISpot) => {
        return { id: Number(item.id), text: item.attributes.name } as Option
      })
      if (leftList) {
        if (newOptions.length > options.length) {
          options.splice(0, newOptions.length, ...newOptions)
        }
      } else {
        options.splice(0, options.length, ...newOptions)
      }
    }

    const isSuitable = (spot: ISpot) => {
      const typeIds = ([] as Array<number>).concat(props.typeId || [])
      if (typeIds.length && !typeIds.includes(spot.attributes.type_id)) {
        return false
      }
      if (
        !props.ignoreStreamFilter &&
        props.stream &&
        spot.attributes.stream !== props.stream
      ) {
        return false
      }
      return true
    }

    // ids go to the query string, so a long pasted list would hit the URL length limit
    const searchByIds = async (ids: Array<number>) => {
      const batches = []
      for (let i = 0; i < ids.length; i += ID_BATCH_SIZE) {
        const batch = ids.slice(i, i + ID_BATCH_SIZE)
        batches.push(
          spotsStore.SearchItems({
            filter: { id: batch },
            limit: batch.length,
            start: 0
          } as ISpotsPayload)
        )
      }
      const responses = await Promise.all(batches)
      return responses.flat().filter(isSuitable)
    }

    const lookup = async (query: string) => {
      currentQuery.value = query
      const ids = parseIdsList(query)
      if (ids) {
        loading.value = true
        assignOptions(await searchByIds(ids), false)
        loading.value = false
      } else if (query.length >= 2) {
        loading.value = true
        const filter = {
          name__or__id: query,
          type_id: props.typeId
        } as Record<string, any>

        if (!props.ignoreStreamFilter) {
          filter.stream = props.stream
        }

        const payload = {
          filter,
          limit: 20,
          start: 0
        } as ISpotsPayload
        const response = await spotsStore.SearchItems(payload)
        assignOptions(response, false)
        loading.value = false
      } else {
        options.splice(0, options.length, ...[])
      }
    }

    const update = (newValue: any) => {
      if (newValue.includes(-1)) {
        const allSpotIds = options.map((opt) => opt.id)
        const filteredValue = newValue.filter((v: number) => v !== -1)
        const mergedValue = [...new Set([...filteredValue, ...allSpotIds])]
        value.value = mergedValue
        context.emit('update:modelValue', mergedValue)
      } else {
        context.emit('update:modelValue', newValue)
      }
    }

    const onPaste = async (event: ClipboardEvent) => {
      const ids = parseIdsList(event.clipboardData?.getData('text') || '')
      if (!ids) {
        return
      }
      event.preventDefault()
      loading.value = true
      const found = await searchByIds(ids)
      loading.value = false

      const foundIds = found.map((spot) => Number(spot.id))
      const skipped = ids.filter((id) => !foundIds.includes(id))
      if (skipped.length) {
        const shown = skipped.slice(0, SKIPPED_SHOWN_LIMIT).join(', ')
        const rest = skipped.length - SKIPPED_SHOWN_LIMIT
        ElMessage.warning(
          `Spots not found or not matching stream/format: ${shown}` +
            (rest > 0 ? ` and ${rest} more` : '')
        )
      }
      if (!foundIds.length) {
        return
      }
      assignOptions(found, false)
      update([...new Set([...(value.value as Array<number>), ...foundIds])])
    }

    const { isDesktop } = useResize()

    loadInitial(false)

    return { loading, lookup, options, update, onPaste, isDesktop, value }
  }
})
</script>

<style lang="scss" scoped>
.spots-selector {
  width: 100%;
}

.el-select {
  width: 100%;
}
</style>
